import styled from '@emotion/styled'

import { COLORS } from '@/styles/colors'

/** 캐러셀 외부 wrapper — 슬라이드들 + 인덱스 */
export const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding-bottom: 8px;
`

/**
 * 가로 스크롤 + scroll-snap — 첫 카드를 "카드 결제" 텍스트 좌측(32px = radio 24 + gap 8)에 맞춰
 * 좌측 정렬(snap start)하고, 카드 폭(224)이 뷰포트보다 작아 다음 카드가 우측에 peek 된다.
 * (center-snap 은 Figma 360 프레임에선 텍스트와 우연히 일치하지만 실기기 폭에선 카드를 중앙으로
 *  밀어 정렬·peek 이 깨지므로 좌측 정렬로 고정한다.)
 * align-self: stretch — CarouselContainer 의 align-items:center 에 눌리지 않고 폭 전체를 차지.
 */
export const SlidesTrack = styled.div`
  display: flex;
  align-self: stretch;
  gap: 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-left: 32px;
  padding-left: 32px;
  padding-right: 32px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`

/** 등록된 카드 슬라이드 — NICEPAY=검정 / KAKAOPAY=노란 (224×140) */
export const RegisteredCardSlide = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isKakaopay',
})<{ isKakaopay: boolean }>`
  position: relative;
  flex: 0 0 224px;
  height: 140px;
  border-radius: 12px;
  background-color: ${({ isKakaopay }) => (isKakaopay ? '#ffeb00' : '#000000')};
  scroll-snap-align: start;
`

/** 카카오페이 등록 카드 — 우상단 카카오페이 로고 */
export const KakaopayBadge = styled.img`
  position: absolute;
  top: 14px;
  right: 16px;
  height: 24px;
  width: auto;
  object-fit: contain;
`

/**
 * 카드사명 — alias 에서 추출한 "[KB국민]". 시안(19008-11669) 카드 상단 가운데.
 * KAKAOPAY 노란 카드는 검정 텍스트.
 */
export const CardIssuer = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isKakaopay',
})<{ isKakaopay: boolean }>`
  position: absolute;
  top: 16px;
  left: 20px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${({ isKakaopay }) => (isKakaopay ? COLORS.textPrimary : COLORS.bgWhite)};
`

/**
 * 카드 alias — 백엔드가 만들어준 "[비씨] 2089" 형태 그대로 표시.
 * 시안의 카드 좌측 하단 텍스트 위치 (Regular). KAKAOPAY 노란 카드는 검정 텍스트.
 */
export const CardAlias = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isKakaopay',
})<{ isKakaopay: boolean }>`
  position: absolute;
  left: 20px;
  bottom: 16px;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ isKakaopay }) => (isKakaopay ? COLORS.textPrimary : COLORS.bgWhite)};
`

/**
 * AddCardSlide 컨테이너 — dashed border 빈 카드 (캐러셀 내 슬라이드).
 *
 * flex: 0 0 100% — SlidesTrack inner 폭(padding 제외)으로 확장.
 * scroll-padding-left(32px) 기준 스냅 시 좌 32px = 우 32px(padding-right) 대칭 → 시각적 중앙.
 * RegisteredCardSlide(224px) 는 그대로 유지해 peek 패턴에 영향 없음.
 */
export const SlideContainer = styled.button`
  display: flex;
  flex: 0 0 100%;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 140px;
  padding: 0;
  border: 2px dashed ${COLORS.strokeInput};
  border-radius: 12px;
  background-color: ${COLORS.bgSubtle};
  cursor: pointer;
  scroll-snap-align: start;
`

/** 카드 0개 empty state — full-width 점선 박스 */
export const EmptyCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 12px;
`

export const EmptyCardButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 140px;
  padding: 0;
  border: 2px dashed ${COLORS.strokeInput};
  border-radius: 12px;
  background-color: ${COLORS.bgSubtle};
  cursor: pointer;
`

export const EmptyCardHint = styled.span`
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: ${COLORS.textMuted};
  text-align: center;
`

export const AddButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background-color: #ff7037;
`

/**
 * PlusIcon — 카드 추가 진입점 ⊕.
 * SVG 인라인 — currentColor로 부모 색 따라감.
 */
export const PlusIcon = styled.span`
  width: 20px;
  height: 20px;
  background-color: ${COLORS.bgWhite};
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 4v12M4 10h12" stroke="black" stroke-width="2" stroke-linecap="round"/></svg>')
    no-repeat center / contain;
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 4v12M4 10h12" stroke="black" stroke-width="2" stroke-linecap="round"/></svg>')
    no-repeat center / contain;
`

export const SlideLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${COLORS.textTertiary};
  text-align: center;
`

/**
 * 페이지네이션 "1 / N" — 카드 영역(224px)과 중앙 정렬.
 * SlidesTrack scroll-padding-left(32px)와 같은 offset + 카드 폭(224px) width로
 * 카드 중심에 text-align: center.
 */
export const Pagination = styled.div`
  align-self: flex-start;
  margin-left: 32px;
  width: 224px;
  display: flex;
  justify-content: center;
  gap: 2px;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.065px;
`

export const PaginationCurrent = styled.span`
  font-weight: 500;
  color: ${COLORS.textSecondary};
`

export const PaginationMuted = styled.span`
  color: ${COLORS.textMuted};
`

/**
 * 카드 영역 하단 보조 문구.
 * AddCardSlide 가 full-width 로 확장되므로 힌트도 full-width center 로 정렬.
 */
export const CardAreaHint = styled.span`
  align-self: stretch;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.textMuted};
`
